import { ChangeEvent, PropsWithChildren, ReactNode } from 'react';
import cx from 'classnames';
import { CheckState } from '@/storybook/components/FormCollection/Checkbox/Checkbox';
import TreeItem from '@/storybook/components/FormCollection/TreeSelect/Components/TreeItem/TreeItem';
import type { TreeItemLevel } from '@/storybook/components/FormCollection/TreeSelect/Components/TreeItem/TreeItem.types';
import styles from './DropdownCheckbox.module.css';

export type DropdownCheckboxProps = {
  label: string;
  optionPath: string;
  onOpenDropdown?: (path: string, currentState: boolean) => void;
  isOpen?: boolean;
  checked: CheckState;
  onCheck: (path: string) => void;
  className?: string;
  rightAdornment?: ReactNode;
  hideCheckbox?: boolean;
  icon?: ReactNode;
  isDisabled?: boolean;
  tooltipText?: string;
  forceTooltip?: boolean;
  level?: TreeItemLevel;
  tabIndex?: number;
};

export default function DropdownCheckbox({
  label,
  optionPath,
  onOpenDropdown,
  isOpen = false,
  checked,
  onCheck,
  className,
  children,
  rightAdornment,
  hideCheckbox = false,
  icon,
  isDisabled = false,
  tooltipText,
  forceTooltip = false,
  level,
  tabIndex,
}: PropsWithChildren<DropdownCheckboxProps>) {
  const isSelected = checked === true;

  const handleToggleDropdown = () => {
    onOpenDropdown?.(optionPath, isOpen);
  };

  const handleCheckChange = (_e?: ChangeEvent<HTMLInputElement>) => {
    if (isDisabled) return;
    onCheck(optionPath);
  };

  const handleLabelClick = () => {
    if (isDisabled) return;
    if (hideCheckbox) {
      onCheck(optionPath);
    }
  };

  return (
    <div
      className={cx(styles.dropdownCheckbox, className)}
      data-is-dropdown-open={isOpen}
      data-testid={`${optionPath}-dropdownCheckbox`}
    >
      <TreeItem
        label={label}
        level={level}
        tabIndex={tabIndex}
        hasChildren={true}
        isExpanded={isOpen}
        onToggleExpand={handleToggleDropdown}
        showCheckbox={!hideCheckbox}
        checked={checked}
        onCheck={handleCheckChange}
        isSelected={isSelected}
        isDisabled={isDisabled}
        tooltipText={tooltipText}
        forceTooltip={forceTooltip}
        onClick={hideCheckbox ? handleLabelClick : undefined}
        badge={rightAdornment}
        icon={icon}
        caretDataTestId={`${optionPath}-dropdownCheckboxArrow`}
        checkboxDataTestId={`${optionPath}-checkbox`}
      />
      {isOpen && <div className={styles.treeChildren}>{children}</div>}
    </div>
  );
}
